import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Collapse,
} from "@mui/material";
import { ChevronDown, ChevronUp, Users, Trash2, TrendingUp } from "lucide-react";
import {
  linkContactToMarketingOrg,
  unlinkContactFromMarketingOrg,
} from "../../../../../../../redux/actions/marketingAction";
import { enqueueSnackbar } from "notistack";

const FicheContacts = ({ org, orgId, allContacts }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(true);

  const orgContacts = org?.contacts || [];
  const linkedIds = new Set(orgContacts.map((c) => c.id));
  const available = (allContacts || []).filter((c) => !linkedIds.has(c.id));

  const handleLink = async (_, contact) => {
    if (!contact) return;
    try {
      await dispatch(linkContactToMarketingOrg(orgId, contact.id));
      enqueueSnackbar("Contact lié", { variant: "success" });
    } catch {
      enqueueSnackbar("Erreur", { variant: "error" });
    }
  };

  const handleUnlink = async (contactId) => {
    try {
      await dispatch(unlinkContactFromMarketingOrg(orgId, contactId));
      enqueueSnackbar("Contact délié", { variant: "success" });
    } catch {
      enqueueSnackbar("Erreur", { variant: "error" });
    }
  };

  return (
    <Box>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          px: 2.5, py: 1, cursor: "pointer",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 1, color: theme.palette.text.secondary }}>
            Contacts
          </Typography>
          <Chip label={orgContacts.length} size="small" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700 }} />
        </Box>
        {expanded ? <ChevronUp size={14} color={theme.palette.text.disabled} /> : <ChevronDown size={14} color={theme.palette.text.disabled} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, pb: 2 }}>
          <Autocomplete
            options={available}
            getOptionLabel={(c) => `${c.first_name || ""} ${c.last_name || ""} — ${c.email}`}
            onChange={handleLink}
            value={null}
            renderInput={(params) => (
              <TextField {...params} placeholder="Lier un contact..." size="small" sx={{ mb: 1 }} />
            )}
            size="small"
            blurOnSelect
            clearOnBlur
          />

          {orgContacts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Users size={24} strokeWidth={1} color={theme.palette.text.disabled} />
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: theme.palette.text.disabled }}>
                Aucun contact lié
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {orgContacts.map((contact) => (
                <Box
                  key={contact.id}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    p: 1, borderRadius: "8px",
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.15s",
                    "&:hover": {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }} noWrap>
                        {[contact.first_name, contact.last_name].filter(Boolean).join(" ") || "—"}
                      </Typography>
                      {contact.team && (
                        <Chip
                          label={contact.team}
                          size="small"
                          sx={{
                            height: 16, fontSize: "0.6rem", fontWeight: 600, px: 0.25,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }} noWrap>
                      {contact.email}
                    </Typography>
                    {contact.phone && (
                      <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontSize: "0.65rem", display: "block" }}>
                        {contact.phone}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                    {(contact.lead_score > 0) && (
                      <Chip
                        icon={<TrendingUp size={10} />}
                        label={contact.lead_score}
                        size="small"
                        sx={{
                          height: 20, fontSize: "0.6rem", fontWeight: 700,
                          bgcolor: alpha("#10b981", 0.1), color: "#10b981",
                          "& .MuiChip-icon": { color: "#10b981" },
                        }}
                      />
                    )}
                    <IconButton size="small" onClick={() => handleUnlink(contact.id)} sx={{ color: theme.palette.text.disabled, "&:hover": { color: "#ef4444" } }}>
                      <Trash2 size={13} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FicheContacts;
